import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Sparkles, Check, Loader2, Mail, ArrowRight, Zap, Crown, Lock } from "lucide-react";
import PayPalCheckoutModal from "./PayPalCheckoutModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_LS_KEY = "kazajah_ai_email";

/**
 * AICreditsPanel — pack picker + balance display.
 *
 * Props:
 * - compact (bool): if true, render a slim horizontal layout (for inline in AIMusicTab)
 * - email (string): controlled email value (optional). If absent, the panel manages its own.
 * - onEmailChange (fn): called when the email is updated
 * - onBalanceChange (fn): called with the latest balance object whenever it refreshes
 * - hidePurchaseUI (bool): if true, only show the balance row (used when the parent has its own buy CTA)
 */
export default function AICreditsPanel({ compact = false, email: emailProp, onEmailChange, onBalanceChange, hidePurchaseUI = false }) {
  const [emailLocal, setEmailLocal] = useState(() => {
    try { return localStorage.getItem(EMAIL_LS_KEY) || ""; } catch { return ""; }
  });
  const email = emailProp !== undefined ? emailProp : emailLocal;
  const setEmail = (v) => {
    if (onEmailChange) onEmailChange(v);
    else setEmailLocal(v);
    try { localStorage.setItem(EMAIL_LS_KEY, v); } catch {}
  };

  const [packs, setPacks] = useState([]);
  const [balance, setBalance] = useState(null);
  const [selectedPack, setSelectedPack] = useState(null);
  const [paypalOpen, setPaypalOpen] = useState(false);
  const [purchaseDone, setPurchaseDone] = useState(false);

  // Fetch packs once
  useEffect(() => {
    fetch(`${API}/ai-credits/packs`).then(r => r.json()).then(setPacks).catch(() => setPacks([]));
  }, []);

  // Fetch balance whenever email changes
  const refreshBalance = useCallback(async () => {
    if (!email || !email.includes("@")) { setBalance(null); return; }
    try {
      const r = await fetch(`${API}/ai-credits/balance?email=${encodeURIComponent(email)}`);
      const b = await r.json();
      setBalance(b);
      onBalanceChange?.(b);
    } catch {}
  }, [email, onBalanceChange]);

  useEffect(() => { refreshBalance(); }, [refreshBalance]);

  const handleSelect = (pack) => {
    if (!email || !email.includes("@")) {
      alert("Renseigne ton email d'abord pour qu'on puisse créditer ton compte.");
      return;
    }
    setSelectedPack(pack);
    setPurchaseDone(false);
    setPaypalOpen(true);
  };

  const handlePostCapture = async () => {
    // PayPal capture endpoint server-side runs the credit grant automatically.
    // We just need to wait briefly then refresh the balance.
    await new Promise(res => setTimeout(res, 1200));
    await refreshBalance();
    setPurchaseDone(true);
  };

  // ─── Balance bar ──────────────────────────────────────────────────────
  const BalanceRow = (
    <div data-testid="ai-credits-balance" className={`${compact ? '' : 'mt-4'} flex flex-wrap items-center gap-3 rounded-2xl border border-amber-500/20 bg-amber-500/5 px-4 py-3`}>
      <Mail size={14} className="text-amber-400 flex-shrink-0" />
      <input
        data-testid="ai-credits-email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="ton.email@exemple.com"
        className="flex-1 min-w-[180px] bg-transparent text-sm text-white placeholder:text-zinc-600 focus:outline-none"
      />
      {balance && balance.email && (
        <>
          <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-300 text-xs font-bold">
            <Sparkles size={12} /> {balance.balance || 0} crédits
          </div>
          {balance.subscription_active && (
            <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-purple-500/15 border border-purple-500/30 text-purple-300 text-xs font-bold">
              <Crown size={12} /> Illimité ({balance.monthly_used || 0}/200)
            </div>
          )}
        </>
      )}
    </div>
  );

  if (compact && hidePurchaseUI) return BalanceRow;

  return (
    <div data-testid="ai-credits-panel" className={compact ? "" : "space-y-6"}>
      {BalanceRow}

      {!compact && (
        <header className="text-center pt-2">
          <p className="text-amber-400 text-xs tracking-[0.3em] uppercase mb-2">Recharge KAZAJAH AI</p>
          <h2 className="text-3xl sm:text-4xl font-black">Choisis ton pack de crédits</h2>
          <p className="text-zinc-500 text-sm mt-2 max-w-md mx-auto">
            🎟 <b className="text-amber-400">Prix de lancement Beta</b> — 1 crédit = 1 génération jusqu'à 60s. Validité 12 mois. La qualité s'améliore chaque mois.
          </p>
        </header>
      )}

      <div className={`grid ${compact ? 'sm:grid-cols-2 lg:grid-cols-5' : 'sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5'} gap-3`}>
        {packs.map((p) => {
          const isSub = !!p.is_subscription;
          const pricePerCredit = isSub ? null : (p.price / p.credits);
          return (
            <motion.button
              key={p.id}
              data-testid={`ai-pack-${p.id}`}
              onClick={() => handleSelect(p)}
              whileHover={{ y: -3 }}
              className={`relative text-left rounded-2xl border p-4 transition-colors ${
                p.featured ? 'border-amber-400 bg-amber-500/10' :
                isSub ? 'border-purple-500/40 bg-purple-500/5 hover:border-purple-400' :
                'border-zinc-800 bg-zinc-950 hover:border-zinc-700'
              }`}
            >
              {p.featured && (
                <span className="absolute -top-2 left-1/2 -translate-x-1/2 text-[9px] uppercase tracking-widest bg-amber-400 text-black font-bold px-2 py-0.5 rounded-full">
                  Best deal
                </span>
              )}
              <div className="flex items-center gap-2 mb-1">
                <span className="text-2xl leading-none">{p.icon || '🎵'}</span>
                <div>
                  <h3 className="font-bold text-sm leading-tight">{p.name}</h3>
                  <p className="text-[10px] text-zinc-500">{p.label}</p>
                </div>
              </div>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="text-2xl font-black">{p.price.toFixed(2)}</span>
                <span className="text-xs text-zinc-500">€{isSub ? ' / mois' : ''}</span>
              </div>
              {isSub ? (
                <p className="text-xs text-purple-300 mt-1 flex items-center gap-1"><Crown size={11} /> Illimité ({p.soft_cap || 200}/mo)</p>
              ) : (
                <p className="text-xs text-zinc-400 mt-1 flex items-center gap-1">
                  <Zap size={11} /> {p.credits} crédits
                  {pricePerCredit && <span className="text-zinc-600 ml-1">({pricePerCredit.toFixed(2)}€/u)</span>}
                </p>
              )}
            </motion.button>
          );
        })}
      </div>

      {paypalOpen && selectedPack && (
        <PayPalCheckoutModal
          product={{ id: selectedPack.id, name: `${selectedPack.name} — ${selectedPack.credits} crédits`, price: selectedPack.price }}
          productType="ai_credit_pack"
          extraOrderFields={{ customer_email: email }}
          onClose={() => setPaypalOpen(false)}
          onBeforeCapture={handlePostCapture}
          onSuccess={() => setPaypalOpen(false)}
        />
      )}

      {purchaseDone && (
        <div data-testid="ai-credits-purchase-success" className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 px-4 py-3 flex items-center gap-2 text-emerald-300 text-sm">
          <Check size={16} /> Achat confirmé ! Tes crédits ont été ajoutés.
        </div>
      )}
    </div>
  );
}
